import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link, useTheme, useMediaQuery, Typography, Stack, Box } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';

import CustomTable, { IColumnType } from '../../../components/CustomTable';
import ListLayout from '../../../components/ListLayout/ListLayout';

import useLocale from '../../../app/hooks/useLocale';

import { ROUTES } from '../../../app/routes';
import moment, { DATE_FORMAT5, serverDateToInputFormat } from '../../../app/utils/dateTimeHelpers';
import { IIEReportsSearchParams } from '../types';
import { useCompletedIEReportsQuery } from '../queries';
import { ClientPublicService } from '../../../app/api/ClientPublicService';

const DEFAULT_PAGE_SIZE = 10;

const CompletedIEReports = () => {
  const { t, getLocalizedDate } = useLocale();
  const theme = useTheme();
  const navigate = useNavigate();

  const isMobile = useMediaQuery(theme.breakpoints.only('mobile'));
  const isDesktop = useMediaQuery(theme.breakpoints.only('desktop'));

  const [searchParams, setSearchParams] = useState<IIEReportsSearchParams>({ maxResultCount: DEFAULT_PAGE_SIZE });

  const { data: reports } = useCompletedIEReportsQuery(searchParams);

  const handleSort = useCallback((column: any, sortDirection: any) => {
    setSearchParams((prev) => ({ ...prev, sorting: `${column} ${sortDirection}` }));
  }, []);

  const getReadableStatus = useCallback(
    (status?: ClientPublicService.IncomeExpensePeriodStatusEnum) => {
      switch (status) {
        case ClientPublicService.IncomeExpensePeriodStatusEnum.Received:
          return t.RECEIVED;
        case ClientPublicService.IncomeExpensePeriodStatusEnum.Completed:
          return t.COMPLETED;
        default:
          return '';
      }
    },
    [t.COMPLETED, t.RECEIVED]
  );

  const columns: IColumnType[] = [
    {
      title: t.PERIOD,
      dataIndex: 'dueDate',
      key: 'period',
      sortable: false,
      render: (date?: moment.Moment) => (
        <>{getLocalizedDate(serverDateToInputFormat(date)?.subtract(1, 'months'), DATE_FORMAT5)}</>
      ),
      width: '45%',
    },
    {
      title: t.STATUS,
      dataIndex: 'status',
      key: 'status',
      sortable: false,
      render: (status?: ClientPublicService.IncomeExpensePeriodStatusEnum) => <>{getReadableStatus(status)}</>,
      width: '45%',
    },
    {
      title: t.ACTION,
      dataIndex: 'action',
      key: 'action',
      sortable: false,
      render: (_: any, item: ClientPublicService.IncomeExpenseSubmissionPeriodDto) => (
        <Link
          component="span"
          sx={{ cursor: 'pointer', color: theme.palette.cobalt, fontWeight: 600 }}
          onClick={() => navigate(`${ROUTES.IE_REPORT}/${item?.externalIeSubmissionPeriodId}`)}
        >
          {t.VIEW}
        </Link>
      ),
    },
  ];

  if (reports === undefined) {
    return <></>;
  }

  return (
    <>
      <Stack spacing={isDesktop ? 0 : 1.5} mt={2}>
        <Typography variant="body3" fontWeight={600} sx={{ color: theme.palette.secondary.main }}>
          {t.COMPLETED} ({reports?.length || 0})
        </Typography>

        {isMobile ? (
          <Box data-testid="list-layout">
            <ListLayout
              items={reports?.map((item: ClientPublicService.IncomeExpenseSubmissionPeriodDto) => ({
                id: item?.externalIeSubmissionPeriodId as string,
                title: (
                  <span style={{ fontWeight: 600 }}>
                    {getLocalizedDate(serverDateToInputFormat(item?.dueDate)?.subtract(1, 'months'), DATE_FORMAT5)}
                  </span>
                ),
                subtitle: (
                  <Stack>
                    <Box sx={{ color: theme.palette.successGreen, fontWeight: 600 }}>
                      {getReadableStatus(item?.status)}
                    </Box>
                  </Stack>
                ),
                actionButtonIcon: <ChevronRight fontSize="large" sx={{ color: theme.palette.cobalt }} />,
                actionButtonProps: {
                  onClick: () => navigate(`${ROUTES.IE_REPORT}/${item?.externalIeSubmissionPeriodId}`),
                },
              }))}
              paginationProps={{ rowsPerPage: DEFAULT_PAGE_SIZE }}
              emptyValue={t.NO_DOCUMENTS_FOUND}
            />
          </Box>
        ) : (
          <Box sx={{ pb: 2 }} data-testid="table-layout">
            <CustomTable
              data={reports}
              columns={columns}
              emptyValue={t.NO_DOCUMENTS_FOUND}
              onColumnSort={handleSort}
              paginationProps={{ rowsPerPage: DEFAULT_PAGE_SIZE }}
            />
          </Box>
        )}
      </Stack>
    </>
  );
};

export default CompletedIEReports;
